import { DialogContentText, type DialogProps } from '@mui/material';

import { useQuery } from '@apollo/client/react';

import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { Or2Button } from '@/components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { type DocumentType, graphql } from '@/gql';
import { Loading } from '@/components/loading/Loading';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { FILE_MANAGER_QUERY } from '@/modules/files/components/FileManager';

import type { AutoCreateFoldersFormData } from './auto-create-folders-dialog/FileManagerAutoCreateFoldersForm';
import {
  AutoCreateFoldersSchema,
  FileManagerAutoCreateFoldersForm,
} from './auto-create-folders-dialog/FileManagerAutoCreateFoldersForm';

const FILE_MANAGER_AUTO_CREATE_FOLDERS_DIALOG_QUERY = graphql(`
  query FileManagerAutoCreateFoldersDialogQuery($groupId: ID!) {
    group(id: $groupId) {
      _id
      committees {
        ...fileManagerAutoCreateFoldersFormCommitteeFields
      }
    }
  }
`);

const FILE_MANAGER_AUTO_CREATE_FOLDERS_DIALOG_MUTATION = graphql(`
  mutation FileManagerAutoCreateFoldersDialogMutation(
    $groupId: ID!
    $newCommittees: [String!]!
    $createCommittees: Boolean!
  ) {
    createCommitteesForGroup(
      params: { groupId: $groupId, committeeNames: $newCommittees }
    ) @include(if: $createCommittees) {
      groupsConnection {
        totalCount
      }
    }

    generateRecommendedFoldersForGroup(params: { group: $groupId }) {
      group {
        _id
      }
    }
  }
`);

const FILE_MANAGER_AUTO_CREATE_FOLDERS_DIALOG_FILES_FOR_OWNER_FRAGMENT =
  graphql(`
    fragment fileManagerAutoCreateFoldersDialogFilesForOwnerFields on FilesForOwner {
      owner {
        ... on Group {
          _id
          name
        }
        ... on Service {
          _id
          name
        }
      }
    }
  `);

interface Props extends DialogProps {
  filesForOwner: DocumentType<
    typeof FILE_MANAGER_AUTO_CREATE_FOLDERS_DIALOG_FILES_FOR_OWNER_FRAGMENT
  >;
  groupReferenceName: string | null;
  onClose: () => void;
}

export const FileManagerAutoCreateFoldersDialog = ({
  filesForOwner,
  groupReferenceName,
  onClose,
  ...other
}: Props) => {
  const { owner } = filesForOwner;

  const { loading: queryLoading, data } = useQuery(
    FILE_MANAGER_AUTO_CREATE_FOLDERS_DIALOG_QUERY,
    {
      variables: { groupId: owner._id ?? '' },
      skip: !owner._id,
      fetchPolicy: 'network-only',
    },
  );

  const { group } = data ?? {};
  const { committees } = group ?? {};

  const { control, handleSubmit, reset } = useForm<AutoCreateFoldersFormData>({
    defaultValues: {
      newCommittees: [],
    },
    resolver: zodResolver(AutoCreateFoldersSchema),
  });

  const [autoCreateFolders, { loading: mutationLoading }] = useMutation(
    FILE_MANAGER_AUTO_CREATE_FOLDERS_DIALOG_MUTATION,
    {
      completionMessage: 'Folders generated successfully!',
      refetchQueries: [
        {
          query: FILE_MANAGER_QUERY,
          variables: {
            ownerId: owner._id ?? '',
            fileId: null,
            includeSharingGroups: false,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: onClose,
    },
  );

  const onSubmit = (data: AutoCreateFoldersFormData) => {
    const createCommittees = data.newCommittees.map(
      (committee) => committee.name,
    );

    autoCreateFolders({
      variables: {
        groupId: owner?._id ?? '',
        newCommittees: createCommittees,
        createCommittees: createCommittees.length > 0,
      },
    });
  };

  return (
    <Or2Dialog
      ariaLabel="file-manager-auto-create-folders"
      loading={mutationLoading}
      slotProps={{ transition: { onEnter: () => reset() } }}
      title={`Automatically create folders for your ${
        groupReferenceName ? `for your ${groupReferenceName}` : ''
      }`}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button
            disabled={mutationLoading}
            onClick={handleSubmit(onSubmit)}
          >
            Create folders!
          </Or2Button>
        </>
      }
      onClose={onClose}
      {...other}
    >
      {queryLoading ? (
        <Loading />
      ) : (
        <>
          <DialogContentText aria-label="file-manager-auto-create-folders-dialog-description">
            {committees?.length === 0 ? (
              <>
                First of all please tell us which committees (if any) you have
              </>
            ) : (
              <>
                We&apos;ll create folders for the committees below, but feel
                free to add to the list
              </>
            )}
          </DialogContentText>
          <FileManagerAutoCreateFoldersForm
            committees={committees}
            control={control}
          />
        </>
      )}
    </Or2Dialog>
  );
};
